import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import PageHeader, { btnPrimary, btnSecondary, inputClass, cardClass } from '../components/PageHeader';

const VendorForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [inviteLink, setInviteLink] = useState('');
  const [portalStatus, setPortalStatus] = useState('');
  const [form, setForm] = useState({
    email: '',
    name: '',
    display_name: '',
    phone: '',
    is_active: true,
  });

  const loadVendor = useCallback(() => {
    if (!isEdit) return;
    apiFetch(`/admin/vendors/${id}`)
      .then((r) => {
        const v = r.data;
        setForm({
          email: v.email || '',
          name: v.user_name || '',
          display_name: v.display_name,
          phone: v.phone || '',
          is_active: v.is_active,
        });
        setInviteLink(v.invite_link || '');
        setPortalStatus(v.portal_status || '');
      })
      .catch(() => toast.error('Failed to load vendor'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    loadVendor();
  }, [loadVendor]);

  const copyInviteLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy — select and copy the link manually');
    }
  };

  const refreshInvite = async () => {
    try {
      const res = await apiFetch(`/admin/vendors/${id}/send-invite`, { method: 'POST' });
      setInviteLink(res.data.invite_link);
      if (res.data.email_sent) {
        toast.success(`Invite email sent to ${res.data.email}`);
      } else {
        toast.success(res.message || 'Invite link ready');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to get invite link');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await apiFetch(`/admin/vendors/${id}`, {
          method: 'PUT',
          body: JSON.stringify({
            display_name: form.display_name,
            phone: form.phone,
            name: form.name,
            is_active: form.is_active,
            email: portalStatus === 'invite_pending' ? form.email : undefined,
          }),
        });
        toast.success('Vendor updated');
        loadVendor();
      } else {
        const res = await apiFetch('/admin/vendors', {
          method: 'POST',
          body: JSON.stringify({
            email: form.email,
            name: form.name,
            display_name: form.display_name,
            phone: form.phone,
          }),
        });
        if (res.data?.email_sent) {
          toast.success(`Vendor created — invite email sent to ${res.data.email}`);
        } else {
          toast.success(res.message || 'Vendor created');
        }
        if (res.data?.invite_link) {
          setInviteLink(res.data.invite_link);
          if (!res.data.email_sent) {
            await copyInviteLink(res.data.invite_link);
          }
          navigate(`/vendors/${res.data.id}/edit`);
          return;
        }
        navigate('/vendors');
      }
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  if (loading) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="max-w-xl">
      <PageHeader
        title={isEdit ? 'Edit team / partner' : 'New team / partner'}
        subtitle={
          isEdit
            ? undefined
            : 'Like the interpreter admin: you add their profile; they set their own password via invite link.'
        }
      />
      <Link to="/vendors" className="text-sm text-teal-700 mb-4 inline-block">
        ← Teams & partners
      </Link>

      {isEdit && portalStatus === 'invite_pending' && (
        <div className={`${cardClass} p-4 mb-4 border-amber-200 bg-amber-50`}>
          <p className="text-sm font-medium text-amber-900">Portal signup pending</p>
          <p className="text-sm text-amber-800 mt-1">
            Send this link so they can create their password (vendor portal signup page coming in Phase 3).
          </p>
          {inviteLink && (
            <p className="text-xs text-slate-700 mt-2 break-all font-mono bg-white p-2 rounded border">
              {inviteLink}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => copyInviteLink(inviteLink)} className={btnSecondary}>
              Copy link
            </button>
            <button type="button" onClick={refreshInvite} className={btnPrimary}>
              Send invite email
            </button>
          </div>
        </div>
      )}

      {isEdit && portalStatus === 'active' && (
        <p className="text-sm text-green-700 mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
          Portal account active — vendor can sign in with their email and password.
        </p>
      )}

      <form onSubmit={submit} className={`${cardClass} p-6 space-y-4`}>
        <div>
          <label className="block text-sm font-medium mb-1">Display name *</label>
          <input
            className={inputClass}
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email *</label>
          <input
            type="email"
            className={inputClass}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            disabled={isEdit && portalStatus === 'active'}
          />
          {!isEdit && (
            <p className="text-xs text-slate-500 mt-1">
              Used for their portal login after they complete the invite link.
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contact name</label>
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Phone</label>
          <input
            className={inputClass}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        )}
        <div className="flex gap-3 pt-2">
          <button type="submit" className={btnPrimary}>
            Save
          </button>
          <Link to="/vendors" className={btnSecondary}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default VendorForm;
